import React, { useEffect, useMemo, useState } from "react";
import { api, formatCurrency, INVESTMENT_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Scale, Save, Plus, Check, Trash2, Loader2, TrendingUp, TrendingDown, ArrowRight, ListChecks, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { useTranslation } from "react-i18next";

const TYPE_COLOR = {
  stock: "#1C7C54",
  crypto: "#B4884A",
  fund: "#4B9471",
  deposit: "#8EBFA6",
  other: "#5F7368",
};

function typeLabel(type, t) {
  // Fallback to INVESTMENT_TYPES so we don't duplicate labels; keep i18n key too.
  const i18nLabel = t(`rebalance.types.${type}`, { defaultValue: "" });
  if (i18nLabel) return i18nLabel;
  return INVESTMENT_TYPES.find((it) => it.value === type)?.label || type;
}

export default function Rebalance() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState({});
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [tolerance, setTolerance] = useState(0.5);
  const [plan, setPlan] = useState(null);
  const [pending, setPending] = useState([]);
  const [selection, setSelection] = useState({});
  const currency = user?.base_currency || "USD";

  const load = async () => {
    try {
      const [tRes, pRes, pdRes] = await Promise.all([
        api.get("/rebalance/targets"),
        api.get("/rebalance/plan"),
        api.get("/rebalance/pending"),
      ]);
      setTargets(tRes.data.targets || {});
      setAllowedTypes(tRes.data.allowed_types || []);
      setTolerance(tRes.data.tolerance_pct || 0.5);
      setPlan(pRes.data);
      setPending(pdRes.data.items || []);
    } catch (e) {
      logger.error("rebalance load", e);
      toast.error(t("rebalance.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPct = useMemo(
    () => allowedTypes.reduce((s, k) => s + (Number(targets[k]) || 0), 0),
    [targets, allowedTypes]
  );
  const sumOk = Math.abs(totalPct - 100) <= 0.5;

  const setTarget = (type, val) => {
    const v = val === "" ? 0 : Number(val);
    setTargets((prev) => ({ ...prev, [type]: isNaN(v) ? 0 : v }));
  };

  const saveTargets = async () => {
    if (!sumOk) {
      toast.error(t("rebalance.sumError", { total: totalPct.toFixed(1) }));
      return;
    }
    setSaving(true);
    try {
      await api.put("/rebalance/targets", { targets });
      const pRes = await api.get("/rebalance/plan");
      setPlan(pRes.data);
      toast.success(t("rebalance.saved"));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("rebalance.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleSel = (idx) => setSelection((s) => ({ ...s, [idx]: !s[idx] }));
  const selectAll = () => {
    const all = {};
    (plan?.trades || []).forEach((_, i) => (all[i] = true));
    setSelection(all);
  };
  const clearSelection = () => setSelection({});

  const saveSelected = async () => {
    const items = (plan?.trades || []).filter((_, i) => selection[i]);
    if (!items.length) return toast.info(t("rebalance.noneSelected"));
    try {
      const r = await api.post("/rebalance/pending", { trades: items });
      toast.success(t("rebalance.saved_n", { count: r.data.created || items.length }));
      setSelection({});
      const pdRes = await api.get("/rebalance/pending");
      setPending(pdRes.data.items || []);
    } catch (e) {
      logger.error(e);
      toast.error(t("rebalance.saveFailed"));
    }
  };

  const toggleDone = async (item) => {
    try {
      const r = await api.patch(`/rebalance/pending/${item.trade_id}`, {
        completed: !item.completed_at,
      });
      setPending((prev) => prev.map((p) => (p.trade_id === item.trade_id ? r.data.item : p)));
    } catch (e) {
      toast.error(t("rebalance.updateFailed"));
    }
  };

  const deletePending = async (item) => {
    try {
      await api.delete(`/rebalance/pending/${item.trade_id}`);
      setPending((prev) => prev.filter((p) => p.trade_id !== item.trade_id));
    } catch (e) {
      toast.error(t("rebalance.deleteFailed"));
    }
  };

  const clearCompleted = async () => {
    try {
      const r = await api.post("/rebalance/pending/clear-completed");
      toast.success(t("rebalance.clearedDone", { count: r.data.deleted || 0 }));
      const pdRes = await api.get("/rebalance/pending");
      setPending(pdRes.data.items || []);
    } catch (e) {
      toast.error(t("rebalance.deleteFailed"));
    }
  };

  if (loading || !plan) {
    return <div className="text-[#5F7368]">{t("common.loading")}</div>;
  }

  const total = plan.total_investments;
  const trades = plan.trades || [];
  const openPending = pending.filter((p) => !p.completed_at).length;
  const donePending = pending.filter((p) => p.completed_at).length;

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2 flex items-center gap-2">
            <Scale className="w-3 h-3" /> {t("rebalance.kicker")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">
            {t("rebalance.title")}
          </h1>
          <p className="text-[#5F7368] mt-2 max-w-2xl">
            {t("rebalance.subtitle")}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.1em] font-bold text-[#8A9992]">
            {t("rebalance.totalInvestments")}
          </div>
          <div className="font-display text-3xl font-semibold tabular-nums text-[#0A110E]" data-testid="rebalance-total">
            {formatCurrency(total, currency)}
          </div>
        </div>
      </div>

      {/* Targets editor */}
      <div className="card-surface p-6 fade-up">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              {t("rebalance.targetsKicker")}
            </p>
            <h2 className="font-display text-lg font-semibold text-[#0A110E]">
              {t("rebalance.targetsTitle")}
            </h2>
          </div>
          <div
            className={`text-sm font-semibold tabular-nums px-3 py-1 rounded-full border ${
              sumOk
                ? "text-[#1C7C54] border-[#1C7C54]/30 bg-[#1C7C54]/5"
                : "text-[#D95D39] border-[#D95D39]/30 bg-[#D95D39]/5"
            }`}
            data-testid="rebalance-sum"
          >
            {totalPct.toFixed(1)}% / 100%
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {allowedTypes.map((type) => (
            <div
              key={type}
              className="border border-[#EBEBEA] rounded-xl p-3 flex items-center gap-3"
            >
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: TYPE_COLOR[type] || "#5F7368" }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0A110E] truncate">
                  {typeLabel(type, t)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={targets[type] ?? 0}
                  onChange={(e) => setTarget(type, e.target.value)}
                  data-testid={`target-input-${type}`}
                  className="w-20 tabular-nums text-right"
                />
                <span className="text-sm text-[#8A9992]">%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button
            onClick={saveTargets}
            disabled={saving || !sumOk}
            data-testid="save-targets-btn"
            className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {t("rebalance.saveTargets")}
          </Button>
          <p className="text-xs text-[#8A9992]">
            {t("rebalance.toleranceHint", { tol: tolerance })}
          </p>
        </div>
      </div>

      {/* Current vs Target */}
      <div className="card-surface p-6 fade-up">
        <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
          {t("rebalance.snapshotKicker")}
        </p>
        <h2 className="font-display text-lg font-semibold text-[#0A110E] mb-4">
          {t("rebalance.snapshotTitle")}
        </h2>

        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-[#8A9992]">
              <tr className="border-b border-[#EBEBEA]">
                <th className="py-2 px-6 font-semibold">{t("rebalance.assetType")}</th>
                <th className="py-2 px-2 text-right font-semibold">{t("rebalance.current")}</th>
                <th className="py-2 px-2 text-right font-semibold">{t("rebalance.target")}</th>
                <th className="py-2 px-2 text-right font-semibold">{t("rebalance.delta")}</th>
                <th className="py-2 px-6 text-right font-semibold">{t("rebalance.action")}</th>
              </tr>
            </thead>
            <tbody data-testid="rebalance-rows">
              {plan.rows.map((r) => {
                const meaningful = Math.abs(r.delta_amount) >= 1;
                const under = meaningful && r.delta_amount > 0;
                const over = meaningful && r.delta_amount < 0;
                // Single lookup replaces the previous nested ternaries.
                const status = under ? "under" : over ? "over" : "ok";
                const deltaClass = {
                  under: "text-[#1C7C54]",
                  over: "text-[#D95D39]",
                  ok: "text-[#8A9992]",
                }[status];
                const renderAction = () => {
                  if (status === "under") {
                    return (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1C7C54]">
                        <TrendingUp className="w-3 h-3" />
                        {t("rebalance.buy")} {formatCurrency(Math.abs(r.delta_amount), currency)}
                      </span>
                    );
                  }
                  if (status === "over") {
                    return (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#D95D39]">
                        <TrendingDown className="w-3 h-3" />
                        {t("rebalance.sell")} {formatCurrency(Math.abs(r.delta_amount), currency)}
                      </span>
                    );
                  }
                  return (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#8A9992]">
                      <CircleCheck className="w-3 h-3" />
                      {t("rebalance.onTarget")}
                    </span>
                  );
                };
                return (
                  <tr key={r.type} className="border-b border-[#EBEBEA]/50">
                    <td className="py-3 px-6">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: TYPE_COLOR[r.type] || "#5F7368" }}
                        />
                        <span className="font-medium text-[#0A110E]">{typeLabel(r.type, t)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right tabular-nums">
                      <div className="text-[#0A110E]">{formatCurrency(r.current_value, currency)}</div>
                      <div className="text-xs text-[#8A9992]">{r.current_pct.toFixed(1)}%</div>
                    </td>
                    <td className="py-3 px-2 text-right tabular-nums">
                      <div className="text-[#0A110E]">{formatCurrency(r.target_value, currency)}</div>
                      <div className="text-xs text-[#8A9992]">{r.target_pct.toFixed(1)}%</div>
                    </td>
                    <td className={`py-3 px-2 text-right tabular-nums font-semibold ${deltaClass}`}>
                      {r.delta_pct > 0 ? "+" : ""}
                      {r.delta_pct.toFixed(1)}%
                    </td>
                    <td className="py-3 px-6 text-right">
                      {renderAction()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suggested trades */}
      <div className="card-surface p-6 fade-up">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              {t("rebalance.tradesKicker")}
            </p>
            <h2 className="font-display text-lg font-semibold text-[#0A110E]">
              {t("rebalance.tradesTitle")}
            </h2>
            <p className="text-xs text-[#5F7368] mt-1">
              {t("rebalance.tradesHint")}
            </p>
          </div>
          {trades.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAll}
                data-testid="select-all-btn"
                className="rounded-xl"
              >
                <Check className="w-3 h-3 mr-1" /> {t("rebalance.selectAll")}
              </Button>
              <Button
                onClick={saveSelected}
                data-testid="save-selected-btn"
                className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl"
                size="sm"
              >
                <Plus className="w-3 h-3 mr-1" /> {t("rebalance.saveSelected")}
              </Button>
            </div>
          )}
        </div>

        {trades.length === 0 ? (
          <div className="py-10 flex flex-col items-center text-center text-[#8A9992]">
            <div className="w-14 h-14 rounded-full bg-[#1C7C54]/10 flex items-center justify-center mb-3">
              <CircleCheck className="w-7 h-7 text-[#1C7C54]" />
            </div>
            <p className="font-semibold text-[#0A110E]">{t("rebalance.balancedTitle")}</p>
            <p className="text-sm mt-1">{t("rebalance.balancedDesc", { tol: tolerance })}</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#EBEBEA]" data-testid="suggested-trades">
            {trades.map((tr, i) => {
              const isBuy = tr.action === "buy";
              return (
                <li key={`${tr.asset_type}-${i}`} className="py-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={!!selection[i]}
                    onChange={() => toggleSel(i)}
                    data-testid={`select-trade-${i}`}
                    className="w-4 h-4 rounded accent-[#1C7C54]"
                  />
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isBuy ? "bg-[#1C7C54]/10 text-[#1C7C54]" : "bg-[#D95D39]/10 text-[#D95D39]"
                    }`}
                  >
                    {isBuy ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#0A110E]">
                      {isBuy ? t("rebalance.buy") : t("rebalance.sell")}{" "}
                      <ArrowRight className="w-3 h-3 inline-block mx-1 text-[#8A9992]" />
                      {typeLabel(tr.asset_type, t)}
                    </div>
                    <div className="text-xs text-[#8A9992]">
                      {t("rebalance.rebalanceHint")}
                    </div>
                  </div>
                  <div className={`text-right tabular-nums font-semibold ${
                    isBuy ? "text-[#1C7C54]" : "text-[#D95D39]"
                  }`}>
                    {formatCurrency(tr.amount, tr.currency || currency)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pending checklist */}
      <div className="card-surface p-6 fade-up">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] flex items-center gap-2">
              <ListChecks className="w-3 h-3" /> {t("rebalance.checklistKicker")}
            </p>
            <h2 className="font-display text-lg font-semibold text-[#0A110E]">
              {t("rebalance.checklistTitle")}
            </h2>
            <p className="text-xs text-[#5F7368] mt-1">
              {t("rebalance.checklistHint")}
            </p>
          </div>
          {donePending > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearCompleted}
              data-testid="clear-completed-btn"
              className="rounded-xl"
            >
              <Trash2 className="w-3 h-3 mr-1" /> {t("rebalance.clearCompleted", { count: donePending })}
            </Button>
          )}
        </div>

        {pending.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#8A9992]">
            {t("rebalance.checklistEmpty")}
          </div>
        ) : (
          <ul className="divide-y divide-[#EBEBEA]" data-testid="pending-list">
            {pending.map((item) => {
              const isBuy = item.action === "buy";
              const done = !!item.completed_at;
              return (
                <li key={item.trade_id} className="py-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={done}
                    onChange={() => toggleDone(item)}
                    data-testid={`pending-check-${item.trade_id}`}
                    className="w-4 h-4 rounded accent-[#1C7C54]"
                  />
                  <div className={`flex-1 min-w-0 ${done ? "opacity-60 line-through" : ""}`}>
                    <div className="font-medium text-[#0A110E]">
                      {isBuy ? t("rebalance.buy") : t("rebalance.sell")}{" "}
                      <ArrowRight className="w-3 h-3 inline-block mx-1 text-[#8A9992]" />
                      {typeLabel(item.asset_type, t)}
                    </div>
                    <div className="text-xs text-[#8A9992]">
                      {new Date(item.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className={`text-right tabular-nums font-semibold ${
                    isBuy ? "text-[#1C7C54]" : "text-[#D95D39]"
                  } ${done ? "opacity-50" : ""}`}>
                    {formatCurrency(item.amount, item.currency || currency)}
                  </div>
                  <button
                    type="button"
                    onClick={() => deletePending(item)}
                    data-testid={`pending-del-${item.trade_id}`}
                    className="text-[#8A9992] hover:text-[#D95D39] p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {(openPending > 0 || donePending > 0) && (
          <div className="mt-4 text-xs text-[#8A9992] flex items-center gap-3">
            <span>{t("rebalance.openCount", { count: openPending })}</span>
            <span>·</span>
            <span>{t("rebalance.doneCount", { count: donePending })}</span>
          </div>
        )}
      </div>
    </div>
  );
}
